/**
 * F-180 (B3) — ProductOffering author/edit modal.
 *
 * Used by `MyProductsPage` for both create and edit. Hands a
 * `ProductInput` payload back to the parent via `onSubmit`; the parent
 * owns the API call so it can refetch the list afterwards.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Spinner,
  Textarea,
} from '@/components/atoms';
import { catalog } from '@/api';
import type { ProductInput } from '@/api/products';
import type { Category, ProductDeliveryMode, ProductOffering } from '@/types/domain';
import { resolveMediaUrl } from '@/utils/media';
import styles from '@/pages/pages.module.css';

export interface ProductEditModalProps {
  open: boolean;
  product?: ProductOffering | null;
  onClose: () => void;
  onSubmit: (payload: ProductInput) => Promise<void>;
}

interface DraftState {
  title: string;
  description: string;
  price_eur: number;
  price_is_from: boolean;
  /** Empty string = unlimited (mapped to null on submit). */
  stock: string;
  category_slug: string;
  is_active: boolean;
  delivery_modes: ProductDeliveryMode[];
  delivery_fee_eur: number;
  image: File | null;
}

const DELIVERY_LABELS: { value: ProductDeliveryMode; label: string }[] = [
  { value: 'pickup', label: 'Самовивіз' },
  { value: 'delivery', label: 'Доставка' },
  { value: 'shipping', label: 'Поштою' },
];

const TITLE_LIMIT = 120;
const DESCRIPTION_LIMIT = 1000;

function emptyDraft(): DraftState {
  return {
    title: '',
    description: '',
    price_eur: 0,
    price_is_from: false,
    stock: '',
    category_slug: '',
    is_active: true,
    delivery_modes: ['pickup'],
    delivery_fee_eur: 0,
    image: null,
  };
}

function draftFromProduct(p: ProductOffering): DraftState {
  return {
    title: p.title,
    description: p.description,
    price_eur: Number(p.price_eur),
    price_is_from: p.price_is_from,
    stock: p.stock == null ? '' : String(p.stock),
    category_slug: p.category_slug,
    is_active: p.is_active,
    delivery_modes: p.delivery_modes?.length ? p.delivery_modes : ['pickup'],
    delivery_fee_eur: p.delivery_fee_eur == null ? 0 : Number(p.delivery_fee_eur),
    image: null,
  };
}

export function ProductEditModal({
  open,
  product,
  onClose,
  onSubmit,
}: ProductEditModalProps): JSX.Element {
  const [draft, setDraft] = useState<DraftState>(emptyDraft());
  const [categories, setCategories] = useState<Category[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    catalog
      .fetchCategories()
      .then((data) => setCategories(data.results))
      .catch(() => setCategories([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (product) {
      setDraft(draftFromProduct(product));
      setPreviewUrl(product.image_url ? (resolveMediaUrl(product.image_url) ?? null) : null);
    } else {
      setDraft(emptyDraft());
      setPreviewUrl(null);
    }
  }, [open, product]);

  // Auto-pick the first available category for new products if none was
  // chosen yet (avoids forcing the user to scroll the select on first use).
  useEffect(() => {
    if (!product && !draft.category_slug && categories.length > 0) {
      const first = categories[0];
      if (first) setDraft((d) => ({ ...d, category_slug: first.slug }));
    }
  }, [product, categories, draft.category_slug]);

  const showsDeliveryFee =
    draft.delivery_modes.includes('delivery') || draft.delivery_modes.includes('shipping');

  const toggleMode = (mode: ProductDeliveryMode): void => {
    setDraft((d) => {
      const next = d.delivery_modes.includes(mode)
        ? d.delivery_modes.filter((m) => m !== mode)
        : [...d.delivery_modes, mode];
      return { ...d, delivery_modes: next };
    });
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] ?? null;
    setDraft((d) => ({ ...d, image: file }));
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!draft.title.trim() || draft.price_eur <= 0 || !draft.category_slug) return;
    setSubmitting(true);
    try {
      const payload: ProductInput = {
        category_slug: draft.category_slug,
        title: draft.title.trim(),
        description: draft.description,
        price_eur: draft.price_eur,
        price_is_from: draft.price_is_from,
        stock: draft.stock === '' ? null : Number(draft.stock),
        is_active: draft.is_active,
        delivery_modes: draft.delivery_modes,
        delivery_fee_eur: showsDeliveryFee ? draft.delivery_fee_eur : null,
      };
      if (draft.image) payload.image = draft.image;
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const titleNearLimit = draft.title.length >= TITLE_LIMIT - 20;
  const descNearLimit = draft.description.length >= DESCRIPTION_LIMIT - 100;

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title={product ? 'Редагувати товар' : 'Новий товар'}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Скасувати
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              submitting ||
              !draft.title.trim() ||
              draft.price_eur <= 0 ||
              !draft.category_slug
            }
          >
            {submitting ? <Spinner size={16} /> : product ? 'Зберегти' : 'Додати'}
          </Button>
        </>
      }
    >
      <div className={styles.formStack}>
        <div>
          <Input
            label="Назва"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value.slice(0, TITLE_LIMIT) })}
            maxLength={TITLE_LIMIT}
            placeholder="Натуральне мило з лавандою"
          />
          {titleNearLimit ? (
            <span className={styles.note}>
              {draft.title.length}/{TITLE_LIMIT}
            </span>
          ) : null}
        </div>

        <div>
          <Textarea
            label="Опис"
            value={draft.description}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value.slice(0, DESCRIPTION_LIMIT) })
            }
            maxLength={DESCRIPTION_LIMIT}
            rows={4}
            placeholder="Склад, розмір, особливості…"
          />
          {descNearLimit ? (
            <span className={styles.note}>
              {draft.description.length}/{DESCRIPTION_LIMIT}
            </span>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="product-image"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--fg-muted)',
              marginBottom: 4,
            }}
          >
            Фото товару
          </label>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Фото"
              style={{
                maxWidth: '100%',
                maxHeight: 180,
                borderRadius: 8,
                marginBottom: 8,
                display: 'block',
              }}
            />
          ) : null}
          <input
            id="product-image"
            type="file"
            accept="image/*"
            onChange={onImageChange}
          />
        </div>

        <div className={styles.formRow}>
          <Input
            label="Ціна (€)"
            type="number"
            value={draft.price_eur}
            onChange={(e) => setDraft({ ...draft, price_eur: Number(e.target.value) })}
            min={0}
            step={0.5}
          />
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={draft.price_is_from}
              onChange={(e) => setDraft({ ...draft, price_is_from: e.target.checked })}
            />
            Ціна &laquo;від&raquo;
          </label>
        </div>

        <div>
          <Input
            label="Залишок (шт.)"
            type="number"
            value={draft.stock}
            onChange={(e) => setDraft({ ...draft, stock: e.target.value })}
            min={0}
            placeholder=""
          />
          <span className={styles.note}>Залиш порожнім для unlimited</span>
        </div>

        <fieldset
          style={{
            border: 0,
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <legend
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--fg-muted)',
              marginBottom: 4,
            }}
          >
            Як отримати?
          </legend>
          {DELIVERY_LABELS.map((opt) => (
            <label
              key={opt.value}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={draft.delivery_modes.includes(opt.value)}
                onChange={() => toggleMode(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </fieldset>

        {showsDeliveryFee ? (
          <Input
            label="Вартість доставки (€)"
            type="number"
            value={draft.delivery_fee_eur}
            onChange={(e) => setDraft({ ...draft, delivery_fee_eur: Number(e.target.value) })}
            min={0}
            step={0.5}
          />
        ) : null}

        <label
          htmlFor="product-category"
          style={{
            display: 'block',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--fg-muted)',
            marginBottom: 4,
          }}
        >
          Категорія
        </label>
        <select
          id="product-category"
          value={draft.category_slug}
          onChange={(e) => setDraft({ ...draft, category_slug: e.target.value })}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--fg)',
            fontSize: 'var(--font-size-base)',
          }}
        >
          {categories.length === 0 ? (
            <option value="">Завантаження…</option>
          ) : (
            categories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name_ua}
              </option>
            ))
          )}
        </select>
      </div>
    </Modal>
  );
}
